"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    Briefcase, FileText, ClipboardList, BarChart2,
    MessageSquare, Euro, HelpCircle, Shield,
    CheckCircle, ArrowRight, Zap
} from "lucide-react";
import Link from "next/link";
import CookieBanner from "@/components/CookieBanner";
import { useRouter } from "next/navigation";
import { supabase } from "@/lib/supabaseClient";

export { metadata } from "./metadata";

// ─── Types ───────────────────────────────────────────────────────────────────
type Lang = "de" | "en";

// ─── Translations ─────────────────────────────────────────────────────────────
const translations = {
    de: {
        nav: {
            howItWorks: "So funktionierts",
            features: "Features",
            pricing: "Preise",
            login: "Login",
        },
        hero: {
            badge: "Marketplace für IT-Freelancer",
            h1: "Finde Projekte.\nVerdiene mehr.",
            sub: "IT-Projekte aus DACH, Europa und weltweit – direkt bewerben, ohne Agentur. Rechnungen, CRM und Steuern inklusive.",
            note: "Kein Middleman · Keine Provision · Kostenlos starten",
            cta1: "Profil erstellen – kostenlos",
            cta2: "Wie funktioniert es?",
        },
        audienceBanner: {
            freelancer: {

                label: "Für Freelancer",
                desc: "Projekte finden & direkt bewerben",
                cta: "Registrieren",
                href: "/register/freelancer",
            },
            company: {

                label: "Für Unternehmen",
                desc: "Freelancer finden & einstellen",
                cta: "Projekt posten",
                href: "/register/company",
            },
        },
        trust: [
            "DSGVO-konform – EU-Server",
            "0 % Provision",
            "Direktkontakt – kein Middleman",

        ],
        pain: {
            h2: "Weniger Chaos. Mehr Aufträge.",
            sub: "Projekte, Rechnungen, CRM und Steuern – alles in einer Plattform. Du fokussierst dich aufs Arbeiten.",
        },
        howItWorks: {
            h2: "So funktioniert FreelanceGen",
            sub: "Profil erstellen → Projekte entdecken → direkt loslegen.",
            steps: [
                { title: "Profil erstellen", desc: "Skills, Verfügbarkeit, Stundensatz – unter 5 Minuten." },
                { title: "Projekte matchen", desc: "Passende Projekte aus DACH, EU & weltweit – gefiltert nach deinem Profil." },
                { title: "Direkt bewerben", desc: "Kein Mittelsmann. Du sprichst direkt mit dem Unternehmen." },
                { title: "Rechnung senden", desc: "DSGVO-konforme PDFs mit SEPA-QR – in Sekunden." },
                { title: "Einnahmen tracken", desc: "CRM, Steuerkalkulator, Aufgaben – alles an einem Ort." },
            ],
            cta: "Jetzt kostenlos starten",
        },
        features: {
            h2: "Alles, was du brauchst",
            sub: "Projekte, Tools, Verwaltung – eine Plattform.",
            groups: [
                {
                    label: "Projekte & Aufträge",
                    features: ["IT-Projekte weltweit", "Direktbewerbung ohne Agentur", "Skills- & Stundensatz-Filter"],
                },
                {
                    label: "Business Tools",
                    features: ["PDF-Rechnungen & Verträge", "SEPA-QR-Code automatisch", "DSGVO-konforme Dokumente"],
                },
                {
                    label: "CRM & Verwaltung",
                    features: ["Kunden & Projekte verwalten", "Aufgaben & Deadlines tracken", "Einkommensrechner (Netto/Brutto)"],
                },
                {
                    label: "Community",
                    features: ["Forum für IT-Freelancer", "Tipps zu Steuern & Recht", "Netzwerk EU & weltweit"],
                },
            ],
        },
        pricing: {
            h2: "Transparent & fair",
            sub: "Ein wachsender IT-Marktplatz für Freelancer und Unternehmen – mit kostenlosem Einstieg für beide Seiten.",
            betaBadge: "Kostenlos starten",
            freelancer: {
                title: "Für Freelancer",
                price: "Kostenlos",
                note: "Während der Beta. Immer.",
                features: ["Projekte & Bewerbungen", "Rechnungen & Verträge", "CRM & Aufgaben", "Einkommensrechner", "Community-Zugang"],
                cta: "Jetzt registrieren",
            },
            company: {
                title: "Für Unternehmen",
                price: "1 Projekt / Monat kostenlos",
                note: "Die erste Projektveröffentlichung pro Kalendermonat ist kostenlos. Weitere Veröffentlichungen können einzeln oder im Paket erworben werden.",
                features: [
                    "Direktbewerbungen erhalten",
                    "Qualifizierte IT-Freelancer",
                    "Kein Recruiting-Aufwand",
                    "Direktkontakt, kein Middleman"
                ],
                cta: "Projekt posten",
                badge: "1 Projekt / Monat kostenlos",
                subNote: "Weitere Veröffentlichungen ab 49 € · 0 % Provision",
            },
            provision: {
                title: "Provision",
                note: "Auf alle Aufträge, für immer.",
                desc: "Du vereinbarst deinen Preis direkt mit dem Unternehmen. FreelanceGen verdient nichts an deinen Aufträgen.",
                trust: "Direkter Kontakt, faire Bedingungen",
            },
        },
        testimonials: {
            h2: "Was Freelancer sagen",
            items: [
                { name: "Anna K.", role: "Software Developerin, Berlin", text: "Innerhalb von 3 Tagen ein Projekt gefunden und die erste Rechnung direkt über die Plattform verschickt. Kein Chaos mehr." },
                { name: "David M.", role: "UX Designer, München", text: "Kein Tool mehr zwischen mir und dem Kunden. Direktkontakt, 0 % Provision – genau das." },
            ],
        },
        faq: {
            h2: "Häufige Fragen",
            items: [
                { q: "Ist FreelanceGen kostenlos?", a: "Ja – während der Beta sind alle Tools und der Projektzugang für Freelancer kostenlos." },
                { q: "Welche Projekte finde ich hier?", a: "IT-Projekte aus DACH, Europa und weltweit – gefiltert nach deinen Skills und Stundensatz." },
                { q: "Gibt es Provisionen?", a: "Nein. Du vereinbarst deinen Preis direkt mit dem Unternehmen. FreelanceGen verdient nichts an deinen Aufträgen." },
                { q: "Wo werden meine Daten gespeichert?", a: "Ausschließlich auf EU-Servern. Vollständig DSGVO-konform." },
            ],
        },
        finalCta: {
            h2: "Starte heute.",
            sub: "Profil erstellen, Projekte matchen, direkt loslegen.",
            note: "Kein Abo · Keine Provision · Kostenlos",
            cta: "Kostenlos registrieren",
        },
    },
    en: {
        nav: {
            howItWorks: "How it works",
            features: "Features",
            pricing: "Pricing",
            login: "Login",
        },
        hero: {
            badge: "Marketplace for IT Freelancers",
            h1: "Find Projects.\nEarn More.",
            sub: "IT projects from DACH, Europe and worldwide – apply directly, no agency. Invoices, CRM and taxes included.",
            note: "No middleman · Zero commission · Free to start",
            cta1: "Create profile – free",
            cta2: "How does it work?",
        },
        audienceBanner: {
            freelancer: {
                label: "For Freelancers",
                desc: "Find projects & apply directly",
                cta: "Sign up",
                href: "/register/freelancer",
            },
            company: {
                label: "For Companies",
                desc: "Find & hire IT freelancers",
                cta: "Post a project",
                href: "/register/company",
            },
        },
        trust: [
            "GDPR-compliant – EU servers",
            "0% commission",
            "Direct contact – no middleman",

        ],
        pain: {
            h2: "Less chaos. More work.",
            sub: "Projects, invoices, CRM and taxes – all in one platform. You focus on the work.",
        },
        howItWorks: {
            h2: "How FreelanceGen works",
            sub: "Create profile → discover projects → get started immediately.",
            steps: [
                { title: "Create profile", desc: "Skills, availability, hourly rate – under 5 minutes." },
                { title: "Match projects", desc: "Relevant projects from DACH, Europe & worldwide – filtered to your profile." },
                { title: "Apply directly", desc: "No middleman. You communicate directly with the company." },
                { title: "Send invoice", desc: "GDPR-compliant PDFs with SEPA QR – in seconds." },
                { title: "Track income", desc: "CRM, tax calculator, tasks – all in one place." },
            ],
            cta: "Get started for free",
        },
        features: {
            h2: "Everything you need",
            sub: "Projects, tools, management – one platform.",
            groups: [
                {
                    label: "Projects & Jobs",
                    features: ["IT projects worldwide", "Direct applications without agencies", "Skills & rate filters"],
                },
                {
                    label: "Business Tools",
                    features: ["PDF invoices & contracts", "Automatic SEPA QR code", "GDPR-compliant documents"],
                },
                {
                    label: "CRM & Management",
                    features: ["Manage clients & projects", "Track tasks & deadlines", "Income calculator (net/gross)"],
                },
                {
                    label: "Community",
                    features: ["Forum for IT freelancers", "Tips on taxes & legal topics", "Network across EU & beyond"],
                },
            ],
        },
        pricing: {
            h2: "Transparent & fair",
            sub: "A growing IT marketplace for freelancers and companies – currently free to use.",
            betaBadge: "Free to get started",
            freelancer: {
                title: "For Freelancers",
                price: "Free",
                note: "Always free for freelancers.",
                features: ["Projects & applications", "Invoices & contracts", "CRM & tasks", "Income calculator", "Community access"],
                cta: "Register now",
            },
            company: {
                title: "For Companies",
                price: "1 project / month free",
                note: "The first project posting per calendar month is free. Additional postings can be purchased individually or as a package.",
                features: [
                    "Receive direct applications",
                    "Qualified IT freelancers",
                    "No recruiting effort",
                    "Direct contact, no middleman"
                ],
                cta: "Post a project",
                badge: "1 project / month free",
                subNote: "Additional postings from €49 · 0% commission",
            },
            provision: {
                title: "Commission",
                note: "On all contracts, forever.",
                desc: "You agree on your price directly with the company. FreelanceGen does not take any commission.",
                trust: "Direct contact, fair conditions",
            },
        },
        testimonials: {
            h2: "What freelancers say",
            items: [
                { name: "Anna K.", role: "Software Developer, Berlin", text: "Found a project in 3 days and sent the first invoice directly through the platform. No more chaos." },
                { name: "David M.", role: "UX Designer, Munich", text: "Finally no tool between me and the client. Direct contact, 0% commission – exactly what I need." },
            ],
        },
        faq: {
            h2: "Common questions",
            items: [
                { q: "Is FreelanceGen free?", a: "Yes – all tools and project access are currently free for freelancers." },
                { q: "What projects can I find here?", a: "IT projects from DACH, Europe and worldwide – filtered by your skills and hourly rate." },
                { q: "Are there any commissions?", a: "No. You agree on your price directly with the company. FreelanceGen does not take any commission." },
                { q: "Where is my data stored?", a: "Exclusively on EU servers. Fully GDPR-compliant." },
            ],
        },
        finalCta: {
            h2: "Start today.",
            sub: "Create a profile, match projects, and get started immediately.",
            note: "No subscription · No commission · Free",
            cta: "Register for free",
        },
    },
} as const;

// ─── Static icon arrays ───────────────────────────────────────────────────────
const featureIcons = [
    <Briefcase className="h-7 w-7 text-blue-600" />,
    <FileText className="h-7 w-7 text-green-600" />,
    <ClipboardList className="h-7 w-7 text-purple-600" />,
    <MessageSquare className="h-7 w-7 text-yellow-600" />,
];

const stepIcons = [
    <ClipboardList className="h-6 w-6 text-blue-600" />,
    <Briefcase className="h-6 w-6 text-blue-600" />,
    <ArrowRight className="h-6 w-6 text-blue-600" />,
    <FileText className="h-6 w-6 text-blue-600" />,
    <BarChart2 className="h-6 w-6 text-blue-600" />,
];

const trustIcons = [
    <Shield className="h-5 w-5 text-green-600" />,
    <Euro className="h-5 w-5 text-teal-600" />,
    <CheckCircle className="h-5 w-5 text-blue-600" />,
    <Zap className="h-5 w-5 text-yellow-500" />,
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function FreelancerLanding() {
    const router = useRouter();
    const [lang, setLang] = useState<Lang>("de");
    const c = translations[lang];

    useEffect(() => {
        const checkUser = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data: profile } = await supabase
                .from("profiles")
                .select("role")
                .eq("id", user.id)
                .single();
            if (profile?.role === "freelancer") router.push("/freelancer-dashboard");
            else if (profile?.role === "company") router.push("/company-dashboard");
        };
        checkUser();
    }, [router]);

    return (
        <div className="min-h-screen bg-white text-gray-900">

            {/* ─── Header ─── */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
                    <Link href="/" className="text-2xl font-bold text-blue-600">FreelanceGen</Link>
                    <nav className="flex items-center gap-3 sm:gap-5">
                        <Link href="#how-it-works" className="text-gray-600 hover:text-blue-600 transition text-sm hidden sm:block">{c.nav.howItWorks}</Link>
                        <Link href="#features" className="text-gray-600 hover:text-blue-600 transition text-sm hidden sm:block">{c.nav.features}</Link>
                        <Link href="#pricing" className="text-gray-600 hover:text-blue-600 transition text-sm hidden sm:block">{c.nav.pricing}</Link>

                        {/* Language switcher */}
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setLang("de")}
                                className={`px-3 py-1 text-sm rounded-md transition ${
                                    lang === "de"
                                        ? "bg-white shadow text-blue-600"
                                        : "text-gray-700 hover:text-blue-600"
                                }`}
                            >
                                DE
                            </button>

                            <button
                                onClick={() => setLang("en")}
                                className={`px-3 py-1 text-sm rounded-md transition ${
                                    lang === "en"
                                        ? "bg-white shadow text-blue-600"
                                        : "text-gray-700 hover:text-blue-600"
                                }`}
                            >
                                EN
                            </button>
                        </div>

                        <Link href="/login" className="bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-blue-700 transition text-sm sm:text-base whitespace-nowrap">
                            {c.nav.login}
                        </Link>
                    </nav>
                </div>
            </header>

            {/* ─── Hero ─── */}
            <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-green-500 text-white pt-28 sm:pt-36 pb-20 sm:pb-28 text-center overflow-hidden">
                <motion.div
                    className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(255,255,255,0.08)_0%,transparent_60%)]"
                    animate={{ scale: [1, 1.06, 1] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10">
                    <motion.div
                        className="inline-flex items-center gap-2 bg-white/15 border border-white/20 px-4 py-1.5 rounded-full text-sm font-medium mb-7"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                        {c.hero.badge}
                    </motion.div>

                    <motion.h1
                        className="text-4xl sm:text-5xl md:text-6xl font-bold mb-5 leading-tight"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                    >
                        {c.hero.h1.split("\n").map((line, i) => (
                            <span key={i}>{line}{i === 0 && <br className="hidden sm:block" />}</span>
                        ))}
                    </motion.h1>

                    <motion.p
                        className="text-lg sm:text-xl text-white/85 mb-3 max-w-xl mx-auto"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.25 }}
                    >
                        {c.hero.sub}
                    </motion.p>

                    <motion.p
                        className="text-white/60 text-sm mb-9"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                    >
                        {c.hero.note}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.45 }}
                        className="flex flex-col sm:flex-row justify-center gap-4"
                    >
                        <Link href="/register/freelancer" className="w-full sm:w-auto">
                            <Button className="bg-white text-blue-700 hover:bg-gray-50 text-base sm:text-lg px-8 py-5 w-full rounded-xl shadow-lg font-semibold transition-all duration-300">
                                {c.hero.cta1}
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                        <Link href="#how-it-works" className="w-full sm:w-auto">
                            <Button className="bg-white/10 border border-white/30 text-white hover:bg-white/20 text-base sm:text-lg px-8 py-5 w-full rounded-xl transition-all duration-300">
                                {c.hero.cta2}
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* ─── Audience split banner ─── */}
            <section className="bg-white border-b border-gray-100">
                <div className="max-w-3xl mx-auto px-4 py-4 grid grid-cols-2 gap-3 sm:gap-6">
                    {(["freelancer", "company"] as const).map((key) => {
                        const item = c.audienceBanner[key];
                        const isFreelancer = key === "freelancer";
                        return (
                            <Link
                                key={key}
                                href={item.href}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 group
                                    ${isFreelancer
                                    ? "border-blue-200 bg-blue-50 hover:bg-blue-100"
                                    : "border-gray-200 bg-gray-50 hover:bg-gray-100"}`}
                            >
                                <span className="text-xl leading-none"></span>
                                <div className="min-w-0">
                                    <div className={`text-sm font-semibold ${isFreelancer ? "text-blue-700" : "text-gray-800"}`}>{item.label}</div>
                                    <div className="text-xs text-gray-500 truncate hidden sm:block">{item.desc}</div>
                                </div>
                                <ArrowRight className={`h-4 w-4 ml-auto flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${isFreelancer ? "text-blue-600" : "text-gray-500"}`} />
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ─── Trust bar ─── */}
            <section className="bg-gray-50 border-b border-gray-100 py-5">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
                        {c.trust.map((text, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                                {trustIcons[i]}
                                <span>{text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Pain → Solution ─── */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-3xl mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-2xl sm:text-3xl font-bold mb-4">{c.pain.h2}</h2>
                        <p className="text-gray-500 text-lg">{c.pain.sub}</p>
                    </motion.div>
                </div>
            </section>

            {/* ─── How it Works ─── */}
            <section id="how-it-works" className="py-16 sm:py-20 bg-gray-50 border-t border-gray-100">
                <div className="max-w-5xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-10 sm:mb-14"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">{c.howItWorks.h2}</h2>
                        <p className="text-gray-500 text-lg">{c.howItWorks.sub}</p>
                    </motion.div>

                    <div className="relative">
                        <div className="hidden lg:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 z-0" />
                        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6 relative z-10">
                            {c.howItWorks.steps.map((step, i) => (
                                <motion.div
                                    key={i}
                                    className="flex flex-col items-center text-center"
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                >
                                    <div className="w-14 h-14 bg-white rounded-full shadow-md border border-blue-100 flex items-center justify-center mb-4">
                                        {stepIcons[i]}
                                    </div>
                                    <div className="text-xs font-bold text-blue-500 mb-1">
                                        {lang === "de" ? `Schritt ${i + 1}` : `Step ${i + 1}`}
                                    </div>
                                    <h3 className="font-bold text-gray-900 mb-1 text-base">{step.title}</h3>
                                    <p className="text-gray-500 text-sm leading-snug">{step.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <div className="text-center mt-12">
                        <Link href="/register/freelancer">
                            <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-xl text-base font-semibold shadow-md">
                                {c.howItWorks.cta}
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ─── Features ─── */}
            <section id="features" className="py-16 sm:py-20 bg-white border-t border-gray-100">
                <div className="max-w-6xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-10 sm:mb-14"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">{c.features.h2}</h2>
                        <p className="text-gray-500 text-lg">{c.features.sub}</p>
                    </motion.div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {c.features.groups.map((group, idx) => (
                            <motion.div
                                key={idx}
                                className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-all duration-300"
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: idx * 0.08 }}
                                whileHover={{ y: -5 }}
                            >
                                <div className="mb-4">{featureIcons[idx]}</div>
                                <h3 className="font-bold text-lg mb-3 text-gray-900">{group.label}</h3>
                                <ul className="space-y-2">
                                    {group.features.map((f, fi) => (
                                        <li key={fi} className="flex items-start gap-2 text-sm text-gray-600">
                                            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Pricing ─── */}
            <section id="pricing" className="py-16 sm:py-20 bg-gray-50 border-t border-gray-100">
                <div className="max-w-5xl mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                            {c.pricing.betaBadge}
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">{c.pricing.h2}</h2>
                        <p className="text-gray-500 text-lg mb-10">{c.pricing.sub}</p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">

                        {/* Freelancer card */}
                        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.05 }}>
                            <Card className="p-7 h-full flex flex-col">
                                <h3 className="text-xl font-bold mb-2">{c.pricing.freelancer.title}</h3>
                                <p className="text-3xl font-bold text-green-600 mb-1">{c.pricing.freelancer.price}</p>
                                <p className="text-xs text-gray-400 mb-4">{c.pricing.freelancer.note}</p>
                                <ul className="space-y-2 text-sm text-gray-600 text-left flex-1">
                                    {c.pricing.freelancer.features.map(f => (
                                        <li key={f} className="flex items-center gap-2">
                                            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <Link href="/register/freelancer" className="mt-6 block">
                                    <Button className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl py-4">
                                        {c.pricing.freelancer.cta}
                                    </Button>
                                </Link>
                            </Card>
                        </motion.div>

                        {/* Company card — beta, no hard pricing */}
                        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}>
                            <Card className="p-7 border-blue-500 border-2 h-full flex flex-col relative overflow-hidden">
                                <div className="absolute top-3 right-3 bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                                    {c.pricing.company.badge}
                                </div>
                                <h3 className="text-xl font-bold mb-2">{c.pricing.company.title}</h3>
                                <p className="text-3xl font-bold text-blue-600 mb-1">{c.pricing.company.price}</p>
                                <p className="text-xs text-gray-400 mb-4">{c.pricing.company.note}</p>
                                <ul className="space-y-2 text-sm text-gray-600 text-left flex-1">
                                    {c.pricing.company.features.map(f => (
                                        <li key={f} className="flex items-center gap-2">
                                            <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <Link href="/register/company" className="mt-6 block">
                                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-4">
                                        {c.pricing.company.cta}
                                    </Button>
                                </Link>
                                <p className="text-xs text-gray-400 mt-3 text-center">{c.pricing.company.subNote}</p>
                            </Card>
                        </motion.div>

                        {/* Provision card */}
                        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.15 }}>
                            <Card className="p-7 h-full flex flex-col">
                                <h3 className="text-xl font-bold mb-2">{c.pricing.provision.title}</h3>
                                <p className="text-5xl font-bold text-teal-600 mb-1">0 %</p>
                                <p className="text-xs text-gray-400 mb-4">{c.pricing.provision.note}</p>
                                <p className="text-sm text-gray-600 text-left flex-1">{c.pricing.provision.desc}</p>
                                <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
                                    <Shield className="h-4 w-4 text-green-500" />
                                    {c.pricing.provision.trust}
                                </div>
                            </Card>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ─── Testimonials ─── */}
            <section className="py-16 sm:py-20 bg-white border-t border-gray-100">
                <div className="max-w-5xl mx-auto px-4">
                    <motion.h2
                        className="text-3xl sm:text-4xl font-bold text-center mb-10"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        {c.testimonials.h2}
                    </motion.h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {c.testimonials.items.map((t, idx) => (
                            <motion.div
                                key={idx}
                                className="bg-gray-50 p-7 rounded-2xl border border-gray-100"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                            >
                                <p className="text-gray-700 mb-5 leading-relaxed">"{t.text}"</p>
                                <div className="font-semibold text-gray-900">{t.name}</div>
                                <div className="text-sm text-gray-500">{t.role}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── FAQ ─── */}
            <section className="py-16 sm:py-20 bg-gray-50 border-t border-gray-100">
                <div className="max-w-4xl mx-auto px-4">
                    <motion.h2
                        className="text-3xl sm:text-4xl font-bold text-center mb-10"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        {c.faq.h2}
                    </motion.h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {c.faq.items.map((faq, idx) => (
                            <motion.div
                                key={idx}
                                className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: idx * 0.07 }}
                            >
                                <h3 className="font-semibold text-gray-900 flex items-start gap-2 mb-2">
                                    <HelpCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                                    {faq.q}
                                </h3>
                                <p className="text-gray-600 text-sm leading-relaxed pl-7">{faq.a}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Final CTA ─── */}
            <section className="py-20 sm:py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-green-500 text-white text-center">
                <div className="max-w-3xl mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{c.finalCta.h2}</h2>
                        <p className="text-lg text-white/85 mb-2">{c.finalCta.sub}</p>
                        <p className="text-white/60 text-sm mb-10">{c.finalCta.note}</p>
                        <Link href="/register/freelancer" className="inline-block w-full sm:w-auto">
                            <Button className="bg-white text-blue-700 hover:bg-gray-50 text-lg px-12 py-6 rounded-xl shadow-2xl font-semibold transition-all duration-300 w-full sm:w-auto">
                                {c.finalCta.cta}
                                <ArrowRight className="h-5 w-5 ml-2" />
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            <CookieBanner />
        </div>
    );
}